import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { useProcessenFilter } from '../hooks/useProcessenFilter';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { isProcessFilterRejected, PROCESS_FILTER_TEKSTEN, ProcessFilters, processFilterTestId } from '../components/ProcessFilters';
import { PROCESS_FILTER_VELDEN } from '../procesLijsten';
import { useProcessesOverview, useRefreshProcessesOverview } from '../hooks/useProcessesOverview';
import { useCoverage } from '../hooks/useCoverage';
import { ProcessCard } from '../components/ProcessCard';
import { FysiekeKetenTegel } from '../components/FysiekeKetenTegel';
import { OverkoepelendBand } from '../components/OverkoepelendBand';
import { formatFetchedAt } from '../format';
import { DIENST_FLOW, DIENST_FLOWS } from '../processes.constants';
import type { DienstFlow } from '../processes.constants';
import type { CanonicalProcessName, DienstProcessName, ProcessSummary } from '../processes.api';

/** Het overzicht heeft geen eigen lijst; alleen de gedeelde Processen-filter leest het adres. */
const GEEN_EIGEN_LIJSTEN = [] as const;

/** Groepskop met chip, zoals de mockup hem zet: het woord plus waar de gegevens vandaan komen. */
function GroupTitle({ title, chip }: { title: string; chip: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--foreground-muted)]">{title}</h3>
      <span className="rounded-full border border-[var(--border)] bg-[var(--background)] px-2 py-0.5 text-[10px] text-[var(--foreground-muted)]">{chip}</span>
    </div>
  );
}

export function ProcessenOverzicht() {
  const { t, i18n } = useTranslation();
  const kopRef = useRef<HTMLHeadingElement>(null);
  const { values: filterValues, setFilters, wis, geweigerd, defaultStilstandDrempelWerkdagen } = useProcessenFilter(GEEN_EIGEN_LIJSTEN);
  const overviewQuery = useProcessesOverview(filterValues);
  const refreshOverview = useRefreshProcessesOverview();
  const coverageQuery = useCoverage();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // CAP-2: een geweigerd filter keert niet terug vóór de filterbalk — die is de weg terug.
  const filterRejected = isProcessFilterRejected(overviewQuery.error, overviewQuery.data?.data.sources.procesManagement);
  if (overviewQuery.isError && !filterRejected) {
    return <ApiErrorMessage error={overviewQuery.error} onRetry={() => overviewQuery.refetch()} />;
  }

  // Bij een weigering door de bron blijven de fysieke tegel en de ketenladder staan (die kennen het
  // filter niet); alleen de ordercijfers — band en dienstkaarten — verdwijnen.
  const data = overviewQuery.data?.data;
  const sourceUnavailable = data?.sources.procesManagement === 'unavailable';

  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await refreshOverview();
    } finally {
      setIsRefreshing(false);
    }
  }

  function coverageSummaryFor(name: CanonicalProcessName): { covered: number; total: number } | null {
    const entry = coverageQuery.data?.data.processes.find((p) => p.name === name);
    if (entry == null || entry.steps.length === 0) return null;
    return { covered: entry.steps.filter((step) => step.coverageState === 'covered').length, total: entry.steps.length };
  }

  function dienstenForFlow(diensten: ProcessSummary[], flow: DienstFlow): ProcessSummary[] {
    return diensten.filter((dienst) => DIENST_FLOW[dienst.name as DienstProcessName] === flow);
  }

  return (
    <div className="space-y-6" data-testid="processes-overview-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 ref={kopRef} tabIndex={-1} className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('processes.title')}</h1>
          <p className="text-xs text-[var(--foreground-muted)]">{t('processes.subtitle')}</p>
        </div>

        {data ? (
          <div className="flex items-center gap-3">
            <p aria-live="polite" className="text-xs text-[var(--foreground-muted)]" data-testid="processes-updated-at">
              {t('processes.updatedAt', { time: formatFetchedAt(data.fetchedAt, i18n.language) })}
            </p>
            <Button data-testid="processes-refresh" disabled={isRefreshing} onClick={handleRefresh} size="sm" variant="secondary">
              <span aria-hidden="true" className="material-symbols-outlined text-[16px]">refresh</span>
              {t('processes.refresh')}
            </Button>
          </div>
        ) : null}
      </header>

      <ProcessFilters defaultStilstandDrempelWerkdagen={defaultStilstandDrempelWerkdagen} onChange={setFilters} values={filterValues} />

      <FilterRejectedMessage error={overviewQuery.error} focusNaWissen={kopRef} linkGeweigerd={geweigerd} onClear={wis} rejected={filterRejected} teksten={PROCESS_FILTER_TEKSTEN} baseTestId={processFilterTestId} velden={PROCESS_FILTER_VELDEN} />

      {overviewQuery.isLoading ? (
        <div className="space-y-4" data-testid="processes-loading">
          <div className="h-32 animate-pulse rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background-muted)]" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <div className="h-48 animate-pulse rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background-muted)]" key={index} />
            ))}
          </div>
        </div>
      ) : null}

      {data ? (
        <>
          {filterRejected ? null : <OverkoepelendBand overview={data.overview} />}

          <section className="space-y-5" data-testid="processes-per-dienst">
            <h2 className="text-sm font-semibold text-[var(--foreground-muted)]">{t('processes.lens.perDienst')}</h2>

            <div className="space-y-2" data-testid="processes-group-physical">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <GroupTitle chip={t('processes.group.physicalChip')} title={t('processes.group.physical')} />
                <div className="flex flex-wrap items-baseline gap-3">
                  {/* spec-processen-actuele-stand (CAP-4): het doelbeeld volgt klant en drempel, niet periode en status. */}
                  <Link
                    className="text-xs text-[var(--secondary)] hover:underline"
                    data-testid="processes-doelbeeld-link"
                    to="/processes/doelbeeld"
                  >
                    {t('processes.physical.linkToTarget')}
                  </Link>
                  {/* Dekking en portfolio zijn eigen schermen naast het herontwerp; zonder deze twee
                      ingangen waren ze na de samenvoeging nergens meer aanklikbaar. */}
                  <Link className="text-xs text-[var(--foreground-muted)] hover:underline" data-testid="processes-dekking-link" to="/processes/coverage">
                    {t('processes.dekking.entryLink')}
                  </Link>
                  {/* De order-portfolio leest dezelfde gedeelde Processen-filter (story 12): geen adresparameters. */}
                  <Link className="text-xs text-[var(--foreground-muted)] hover:underline" data-testid="processes-portfolio-link" to="/processes/portfolio">
                    {t('processes.portfolio.entryLink')}
                  </Link>
                </div>
              </div>
              <FysiekeKetenTegel chain={data.physicalChain} eventsUnavailable={data.sources.procesManagementEvents?.status === 'unavailable'} filters={filterValues} process={data.physical} />
            </div>

            {(filterRejected ? [] : DIENST_FLOWS).map((flow) => {
              const items = dienstenForFlow(data.diensten, flow);
              if (items.length === 0) return null;
              return (
                <div className="space-y-2" data-testid={`processes-group-${flow}`} key={flow}>
                  <GroupTitle chip={t(`processes.group.${flow}Chip`)} title={t(`processes.group.${flow}`)} />
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid={`processes-grid-${flow}`}>
                    {items.map((process) => (
                      <ProcessCard key={process.name} process={process} sourceUnavailable={sourceUnavailable} />
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        </>
      ) : null}
    </div>
  );
}
